/**
 * Welle 119 — Stunden aus der Arbeitszeit (Arbeitsnachweis) als Rechnungspositionen übernehmen.
 * Filter Mitarbeiter / Zeitraum / Arbeitsort, Häkchen je Eintrag, Stundenpreis je Rechnung eintippen,
 * „je Eintrag eine Position" oder „je Mitarbeiter + Tätigkeit zusammengefasst". Abgerechnete Einträge (beim Stellen
 * markiert) und Einträge in anderen Entwürfen sind gesperrt — keine Doppelabrechnung.
 */
import React, { useEffect, useMemo, useState } from "react";
import { API, json, zahl, euro, deDatum } from "./hilfen";

const deZahl = (v) => (Math.round(zahl(v) * 100) / 100).toLocaleString("de-DE", { maximumFractionDigits: 2 });

export default function ArbeitszeitAuswahl({ form, schonDrin, onClose, onUebernehmen }) {
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [ma, setMa] = useState([]);
  const [von, setVon] = useState(form.leistung_von || "");
  const [bis, setBis] = useState(form.leistung_bis || "");
  const [ort, setOrt] = useState("");
  const [daten, setDaten] = useState({ eintraege: [], orte: [] });
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState("");
  const [gewaehlt, setGewaehlt] = useState(new Set());
  const [preis, setPreis] = useState("");
  const [modus, setModus] = useState("einzeln");
  const [satz, setSatz] = useState(form.vorlagen?.satz_regel ?? 19);

  useEffect(() => {
    fetch(`${API}/arbeitszeit/mitarbeiter`).then(json).then((d) => setMitarbeiter(d.mitarbeiter || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLaden(true); setFehler("");
      try {
        const q = new URLSearchParams({ ma: ma.join(","), von, bis, ort });
        if (form.id) q.set("rid", form.id);
        setDaten(await json(await fetch(`${API}/arbeitszeit?${q}`)));
      } catch (e) { setFehler(String(e.message || e)); }
      setLaden(false);
    }, 250);
    return () => clearTimeout(t);
  }, [ma, von, bis, ort, form.id]);

  const key = (e) => `${e.tabelle}:${e.id}`;
  const sperre = (e) => e.abgerechnet ? `abgerechnet ${e.abgerechnet}` : e.reserviert ? `in ${e.reserviert}` : schonDrin.has(key(e)) ? "schon in dieser Rechnung" : "";
  const frei = daten.eintraege.filter((e) => !sperre(e));
  const auswahl = daten.eintraege.filter((e) => gewaehlt.has(key(e)));
  const stunden = auswahl.reduce((a, e) => a + e.stunden, 0);

  const umschalten = (e) => setGewaehlt((g) => { const n = new Set(g); n.has(key(e)) ? n.delete(key(e)) : n.add(key(e)); return n; });
  const alle = () => setGewaehlt((g) => (frei.every((e) => g.has(key(e))) ? new Set() : new Set(frei.map(key))));

  const positionen = useMemo(() => {
    const p = zahl(preis);
    if (modus === "einzeln") {
      return auswahl.map((e) => ({
        text: `${deDatum(e.datum)} · ${e.name}${e.taetigkeit ? ` · ${e.taetigkeit}` : ""}${e.arbeitsort ? ` (${e.arbeitsort})` : ""}`,
        menge: Math.round(e.stunden * 100) / 100, einheit: "Std.", preis: p, satz, quelle: "arbeitszeit", az: [[e.tabelle, e.id]],
      }));
    }
    const gruppen = new Map();
    auswahl.forEach((e) => {
      const k = `${e.kuerzel}|${e.taetigkeit}`;
      if (!gruppen.has(k)) gruppen.set(k, []);
      gruppen.get(k).push(e);
    });
    return [...gruppen.values()].map((liste) => {
      const e0 = liste[0];
      const tage = liste.map((e) => e.datum).sort();
      const zeitraum = tage.length > 1 ? `${deDatum(tage[0])}–${deDatum(tage[tage.length - 1])}` : deDatum(tage[0]);
      return {
        text: `${e0.name}${e0.taetigkeit ? ` · ${e0.taetigkeit}` : ""} · ${liste.length} Einsatz${liste.length > 1 ? "e" : ""} ${zeitraum}`,
        menge: Math.round(liste.reduce((a, e) => a + e.stunden, 0) * 100) / 100, einheit: "Std.", preis: p, satz,
        quelle: "arbeitszeit", az: liste.map((e) => [e.tabelle, e.id]),
      };
    });
  }, [auswahl, preis, modus, satz]);

  return (
    <div className="rg-modal-hg" onClick={onClose}>
      <div className="rg-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rg-modal-kopf">
          <b>⏱ Arbeitszeit als Positionen übernehmen</b>
          <button type="button" className="mb-link" onClick={onClose}>✕</button>
        </div>
        <div className="rg-az-filter">
          <div className="rg-az-ma">
            {mitarbeiter.map((m) => (
              <label key={m.kuerzel} className={`rg-chip${ma.includes(m.kuerzel) ? " an" : ""}`}>
                <input type="checkbox" checked={ma.includes(m.kuerzel)}
                  onChange={() => setMa((x) => (x.includes(m.kuerzel) ? x.filter((k) => k !== m.kuerzel) : [...x, m.kuerzel]))} />
                {m.kuerzel} · {m.name}
              </label>
            ))}
            <span className="rg-klein" style={{ margin: 0 }}>{ma.length ? "" : "(keiner gewählt = alle)"}</span>
          </div>
          <label className="mb-feld"><span className="mb-label">von</span><input type="date" className="mb-input" value={von} onChange={(e) => setVon(e.target.value)} /></label>
          <label className="mb-feld"><span className="mb-label">bis</span><input type="date" className="mb-input" value={bis} onChange={(e) => setBis(e.target.value)} /></label>
          <label className="mb-feld"><span className="mb-label">Arbeitsort / Projekt</span>
            <input className="mb-input" list="rg-az-orte" value={ort} placeholder="alle" onChange={(e) => setOrt(e.target.value)} />
            <datalist id="rg-az-orte">{daten.orte.map((o) => <option key={o} value={o} />)}</datalist>
          </label>
        </div>
        {fehler && <div className="mb-meldung fehler">{fehler}</div>}
        <div className="rg-tabelle-rahmen rg-az-liste">
          <table className="rg-tabelle">
            <thead><tr>
              <th><input type="checkbox" checked={frei.length > 0 && frei.every((e) => gewaehlt.has(key(e)))} onChange={alle} title="alle freien" /></th>
              <th>Datum</th><th>MA</th><th>Zeit</th><th className="r">Std.</th><th>Arbeitsort</th><th>Tätigkeit</th><th>Status</th>
            </tr></thead>
            <tbody>
              {daten.eintraege.map((e) => {
                const sp = sperre(e);
                return (
                  <tr key={key(e)} className={sp ? "rg-az-gesperrt" : ""} onClick={() => !sp && umschalten(e)}>
                    <td><input type="checkbox" disabled={!!sp} checked={gewaehlt.has(key(e))} onChange={() => umschalten(e)} onClick={(x) => x.stopPropagation()} /></td>
                    <td>{deDatum(e.datum)}</td><td>{e.kuerzel}</td><td>{e.von && e.bis ? `${e.von}–${e.bis}` : ""}</td>
                    <td className="r">{deZahl(e.stunden)}</td><td>{e.arbeitsort}</td><td className="rg-inhalt">{e.taetigkeit}</td>
                    <td className="rg-klein" style={{ margin: 0 }}>{sp}</td>
                  </tr>
                );
              })}
              {!daten.eintraege.length && <tr><td colSpan={8} className="rg-klein">{laden ? "lädt …" : "Keine Einträge mit Stunden im Zeitraum."}</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="rg-az-fuss">
          <label className="mb-feld"><span className="mb-label">Stundenpreis netto (€)</span>
            <input className="mb-input rg-zahl" value={preis} placeholder="z. B. 30" onChange={(e) => setPreis(e.target.value)} />
          </label>
          <label className="mb-feld"><span className="mb-label">USt %</span>
            <select className="mb-input rg-satz" value={satz} onChange={(e) => setSatz(Number(e.target.value))}>
              {[19, 7, 0].map((x) => <option key={x} value={x}>{x} %</option>)}
            </select>
          </label>
          <label className="mb-feld"><span className="mb-label">Positionen</span>
            <select className="mb-input" value={modus} onChange={(e) => setModus(e.target.value)}>
              <option value="einzeln">je Eintrag eine Position</option>
              <option value="gruppe">je Mitarbeiter + Tätigkeit zusammengefasst</option>
            </select>
          </label>
          <div className="rg-klein" style={{ margin: 0, alignSelf: "center" }}>
            {auswahl.length} Einträge · {deZahl(stunden)} Std. → {positionen.length} Position(en) · {euro(stunden * zahl(preis))} netto
          </div>
          <button type="button" className="mb-btn primary" disabled={!positionen.length || !zahl(preis)}
            title={zahl(preis) ? "" : "Bitte einen Stundenpreis eintragen"} onClick={() => onUebernehmen(positionen)}>Übernehmen</button>
        </div>
        <p className="rg-klein">Die Einträge werden beim <b>Stellen</b> der Rechnung als abgerechnet markiert (Storno gibt sie wieder frei).
          Text, Menge und Preis sind danach in der Rechnung frei änderbar.</p>
      </div>
    </div>
  );
}
